import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Lightbulb as LightbulbIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { styled } from '@mui/material/styles';

import PageHeader from '../../components/PageHeader';
import {
  EnrichmentAnalyticsDocument,
  EnrichmentCostAnalysisDocument,
  EnrichmentTimeSeriesDocument,
  EnrichmentLayerSuccessDocument,
} from '../../gql/generated/graphql';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    transform: 'translateY(-4px)',
  },
}));

const GradientCard = styled(Card)<{ gradient?: string }>(({ theme, gradient }) => ({
  height: '100%',
  borderRadius: 16,
  background:
    gradient ||
    `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
  color: '#FFFFFF',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    transform: 'translateY(-4px)',
  },
}));

const MetricBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

// Helper functions
const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toFixed(0);
};

const formatPercentage = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  return `${num.toFixed(1)}%`;
};

const formatCurrency = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  return `$${num.toFixed(2)}`;
};

const formatMilliseconds = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return 'N/A';
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${ms.toFixed(0)}ms`;
};

// Insight priority colors
const getInsightColor = (type: string) => {
  switch (type) {
    case 'success':
      return { bg: alpha('#4CAF50', 0.1), color: '#2E7D32', icon: CheckCircleIcon };
    case 'info':
      return { bg: alpha('#2196F3', 0.1), color: '#1565C0', icon: InfoIcon };
    case 'warning':
      return { bg: alpha('#FF9800', 0.1), color: '#E65100', icon: WarningIcon };
    case 'error':
      return { bg: alpha('#F44336', 0.1), color: '#C62828', icon: ErrorIcon };
    default:
      return { bg: alpha('#9E9E9E', 0.1), color: '#424242', icon: LightbulbIcon };
  }
};

// Time range selector
const TimeRangeSelector = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const ranges = [
    { value: '1h', label: '1 Hour' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
  ];

  return (
    <Stack direction="row" spacing={1}>
      {ranges.map(range => (
        <Chip
          key={range.value}
          label={range.label}
          variant={value === range.value ? 'filled' : 'outlined'}
          color={value === range.value ? 'primary' : 'default'}
          onClick={() => onChange(range.value)}
          sx={{ cursor: 'pointer' }}
        />
      ))}
    </Stack>
  );
};

// Main component
const EnrichmentPerformancePage = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('24h');

  // Fetch analytics data
  const {
    loading: analyticsLoading,
    error: analyticsError,
    data: analyticsData,
    refetch: refetchAnalytics,
  } = useQuery(EnrichmentAnalyticsDocument, {
    variables: { timeRange },
    pollInterval: 60000, // 1 minute
    fetchPolicy: 'cache-and-network',
  });

  // Fetch cost analysis
  const {
    loading: costLoading,
    error: costError,
    data: costData,
    refetch: refetchCost,
  } = useQuery(EnrichmentCostAnalysisDocument, {
    variables: { timeRange: '30d' },
    fetchPolicy: 'cache-and-network',
  });

  // Fetch time series
  const {
    loading: timeSeriesLoading,
    error: timeSeriesError,
    data: timeSeriesData,
    refetch: refetchTimeSeries,
  } = useQuery(EnrichmentTimeSeriesDocument, {
    variables: { timeRange },
    fetchPolicy: 'cache-and-network',
  });

  // Fetch layer success data
  const {
    loading: layerSuccessLoading,
    error: layerSuccessError,
    data: layerSuccessData,
    refetch: refetchLayerSuccess,
  } = useQuery(EnrichmentLayerSuccessDocument, {
    variables: { timeRange },
    pollInterval: 60000, // 1 minute
    fetchPolicy: 'cache-and-network',
  });

  const handleRefresh = () => {
    refetchAnalytics();
    refetchCost();
    refetchTimeSeries();
    refetchLayerSuccess();
  };

  const loading = analyticsLoading && !analyticsData;
  const hasError = (analyticsError || costError || timeSeriesError) && !analyticsData;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography variant="h6" color="error">
          Error loading enrichment analytics
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {analyticsError?.message || costError?.message || timeSeriesError?.message}
        </Typography>
        <Button variant="outlined" color="primary" onClick={handleRefresh} sx={{ mt: 2 }} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  const analytics = analyticsData?.enrichmentAnalytics;
  const cost = costData?.enrichmentCostAnalysis;
  const timeSeries = timeSeriesData?.enrichmentTimeSeries;
  const layerSuccess = layerSuccessData?.enrichmentLayerSuccess;

  return (
    <Box>
      <PageHeader
        title="Contact Enrichment Performance"
        subtitle="Monitor enrichment pipeline performance, costs, and data quality"
        variant="default"
      />

      <Box sx={{ p: 3 }}>
        {/* Header Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Box>

        {/* SUCCESS RATE & DATA COVERAGE - PRIMARY FOCUS */}
        {layerSuccess && (
          <>
            {/* Overall Success Rate - Hero Card */}
            <StyledCard sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />
                  <Typography variant="h5" fontWeight={700}>
                    Enrichment Success Metrics
                  </Typography>
                </Box>

                <Grid2 container spacing={3}>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    <Box textAlign="center">
                      <Typography variant="h1" fontWeight={700} color="success.main" sx={{ fontSize: '3.5rem' }}>
                        {formatPercentage(layerSuccess.overallSuccessRate)}
                      </Typography>
                      <Typography variant="h6" color="text.secondary" fontWeight={500}>
                        Overall Success Rate
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {formatNumber(layerSuccess.totalEnrichmentsAnalyzed)} enrichments analyzed
                      </Typography>
                    </Box>
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 8 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                      Data Type Coverage
                    </Typography>
                    <Grid2 container spacing={2}>
                      {/* Contact Email Coverage */}
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <MetricBox>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            📧 Contact Emails
                          </Typography>
                          <Box display="flex" alignItems="baseline" gap={1} mt={1}>
                            <Typography variant="h4" fontWeight={700} color="primary.main">
                              {formatPercentage(layerSuccess.contactEmailCoverage?.coverageRate)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({formatNumber(layerSuccess.contactEmailCoverage?.totalFound)} found)
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={0.5}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Existing DB:
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {formatNumber(layerSuccess.contactEmailCoverage?.existingCount)}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Firecrawl:
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {formatNumber(layerSuccess.contactEmailCoverage?.firecrawlCount)}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Apollo:
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {formatNumber(layerSuccess.contactEmailCoverage?.apolloCount)}
                              </Typography>
                            </Box>
                          </Stack>
                        </MetricBox>
                      </Grid2>

                      {/* Managing Director Coverage */}
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <MetricBox>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            👔 Managing Director Info
                          </Typography>
                          <Box display="flex" alignItems="baseline" gap={1} mt={1}>
                            <Typography variant="h4" fontWeight={700} color="primary.main">
                              {formatPercentage(layerSuccess.managingDirectorCoverage?.coverageRate)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({formatNumber(layerSuccess.managingDirectorCoverage?.totalFound)} found)
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={0.5}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Existing DB:
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {formatNumber(layerSuccess.managingDirectorCoverage?.existingCount)}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Firecrawl:
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {formatNumber(layerSuccess.managingDirectorCoverage?.firecrawlCount)}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Apollo:
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {formatNumber(layerSuccess.managingDirectorCoverage?.apolloCount)}
                              </Typography>
                            </Box>
                          </Stack>
                        </MetricBox>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Grid2>
              </CardContent>
            </StyledCard>

            {/* Layer Performance Breakdown */}
            <StyledCard sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={3}>
                  Layer Performance Analysis
                </Typography>
                <Grid2 container spacing={3}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MetricBox>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <StorageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Existing Database
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {formatPercentage(layerSuccess.existingPerformance?.successRate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Success Rate
                      </Typography>
                      <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                          {formatNumber(layerSuccess.existingPerformance?.successfulExtractions)} successful extractions
                        </Typography>
                      </Box>
                    </MetricBox>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MetricBox>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <TrendingUpIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Firecrawl (MongoDB Cache)
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        {formatPercentage(layerSuccess.firecrawlPerformance?.successRate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Success Rate
                      </Typography>
                      <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                          {formatNumber(layerSuccess.firecrawlPerformance?.successfulExtractions)} successful
                          extractions
                        </Typography>
                      </Box>
                    </MetricBox>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MetricBox>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <SpeedIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Apollo Layer
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {formatPercentage(layerSuccess.apolloPerformance?.successRate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Success Rate
                      </Typography>
                      <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                          {formatNumber(layerSuccess.apolloPerformance?.successfulExtractions)} successful extractions
                        </Typography>
                      </Box>
                    </MetricBox>
                  </Grid2>
                </Grid2>
              </CardContent>
            </StyledCard>
          </>
        )}

        {/* Key Metrics Summary */}
        <Grid2 container spacing={3} mb={3}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <GradientCard>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SpeedIcon sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Total Enrichments
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {formatNumber(analytics?.summary?.totalEnrichments)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  in {analytics?.timeRange}
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <GradientCard
              gradient={`linear-gradient(135deg, ${alpha('#FF9800', 0.9)} 0%, ${alpha('#F57C00', 0.95)} 100%)`}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TrendingUpIcon sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Avg Processing Time
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {formatMilliseconds(analytics?.summary?.avgTotalTimeMs)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  end-to-end latency
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <GradientCard
              gradient={`linear-gradient(135deg, ${alpha('#4CAF50', 0.9)} 0%, ${alpha('#388E3C', 0.95)} 100%)`}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <StorageIcon sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Cache Hit Rate
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {formatPercentage(analytics?.summary?.avgCacheHitRate)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  MongoDB cache efficiency
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <GradientCard
              gradient={`linear-gradient(135deg, ${alpha('#9C27B0', 0.9)} 0%, ${alpha('#7B1FA2', 0.95)} 100%)`}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachMoneyIcon sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Total Cost (30d)
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {formatCurrency(cost?.costs?.totalCost)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Firecrawl + Apollo credits
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid2>
        </Grid2>

        {/* Performance Breakdown */}
        <Grid2 container spacing={3} mb={3}>
          {/* Layer Timing */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Layer Performance Breakdown
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Existing Data Layer
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        {formatPercentage(analytics?.layerTiming?.existingPercent)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analytics?.layerTiming?.existingPercent || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatMilliseconds(analytics?.summary?.avgExistingTimeMs)}
                    </Typography>
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Firecrawl Layer
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="warning.main">
                        {formatPercentage(analytics?.layerTiming?.firecrawlPercent)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analytics?.layerTiming?.firecrawlPercent || 0}
                      color="warning"
                      sx={{
                        height: 8,
                        borderRadius: 4,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatMilliseconds(analytics?.summary?.avgFirecrawlTimeMs)}
                    </Typography>
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Apollo Layer
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {formatPercentage(analytics?.layerTiming?.apolloPercent)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analytics?.layerTiming?.apolloPercent || 0}
                      color="success"
                      sx={{
                        height: 8,
                        borderRadius: 4,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatMilliseconds(analytics?.summary?.avgApolloTimeMs)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid2>

          {/* Method Breakdown */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Extraction Method Usage
                </Typography>
                {analytics?.methodBreakdown && analytics.methodBreakdown.length > 0 ? (
                  <BarChart
                    dataset={analytics.methodBreakdown.map(m => ({
                      method: m?.method || 'unknown',
                      count: m?.count || 0,
                      percentage: m?.percentage || 0,
                    }))}
                    xAxis={[{ scaleType: 'band', dataKey: 'method' }]}
                    series={[
                      {
                        dataKey: 'count',
                        label: 'Enrichments',
                        color: theme.palette.primary.main,
                      },
                    ]}
                    height={200}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No method breakdown data available
                  </Typography>
                )}
              </CardContent>
            </StyledCard>
          </Grid2>
        </Grid2>

        {/* Cost Analysis */}
        <Grid2 container spacing={3} mb={3}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={3}>
                  API Cost Breakdown (30 days)
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MetricBox>
                      <Typography variant="caption" color="text.secondary">
                        Firecrawl Cost
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="warning.main">
                        {formatCurrency(cost?.costs?.firecrawlCost)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(cost?.usage?.firecrawlCreditsUsed)} credits
                      </Typography>
                    </MetricBox>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MetricBox>
                      <Typography variant="caption" color="text.secondary">
                        Apollo Cost
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {formatCurrency(cost?.costs?.apolloCost)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(cost?.usage?.apolloCreditsUsed)} credits
                      </Typography>
                    </MetricBox>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MetricBox>
                      <Typography variant="caption" color="text.secondary">
                        Cost per Enrichment
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="primary.main">
                        {formatCurrency(cost?.costs?.avgCostPerEnrichment)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        average
                      </Typography>
                    </MetricBox>
                  </Grid2>
                </Grid2>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Cache Impact
                  </Typography>
                  <Grid2 container spacing={2}>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Cache Hit Rate
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatPercentage(cost?.cacheImpact?.cacheHitRate)}
                      </Typography>
                    </Grid2>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Estimated Savings
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">
                        {formatCurrency(cost?.cacheImpact?.estimatedSavings)}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Monthly Projection
                </Typography>
                <Typography variant="h3" fontWeight={700} color="primary.main" mb={1}>
                  {formatCurrency(cost?.projections?.monthlyCostProjection)}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Based on current usage trends
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Recommendations
                </Typography>
                <Stack spacing={1}>
                  {cost?.recommendations?.map((rec, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Typography variant="caption">{rec}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid2>
        </Grid2>

        {/* Time Series Chart */}
        {timeSeries?.dataPoints && timeSeries.dataPoints.length > 0 && (
          <StyledCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Enrichment Activity Over Time
              </Typography>
              <LineChart
                dataset={timeSeries.dataPoints.map(dp => ({
                  timestamp: new Date(dp?.timestamp || 0).getTime(),
                  count: dp?.count || 0,
                  avgTimeMs: dp?.avgTimeMs || 0,
                  avgCacheRate: (dp?.avgCacheRate || 0) * 100,
                }))}
                xAxis={[
                  {
                    scaleType: 'time',
                    dataKey: 'timestamp',
                    valueFormatter: value => new Date(value).toLocaleDateString(),
                  },
                ]}
                series={[
                  {
                    dataKey: 'count',
                    label: 'Enrichments',
                    color: theme.palette.primary.main,
                    showMark: false,
                  },
                ]}
                height={300}
              />
            </CardContent>
          </StyledCard>
        )}

        {/* Insights */}
        {analytics?.insights && analytics.insights.length > 0 && (
          <StyledCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LightbulbIcon color="warning" />
                <Typography variant="h6" fontWeight={700}>
                  Performance Insights
                </Typography>
              </Box>
              <Grid2 container spacing={2}>
                {analytics.insights.map((insight, index) => {
                  const colors = getInsightColor(insight?.type || 'info');
                  const Icon = colors.icon;
                  return (
                    <Grid2 size={{ xs: 12, md: 6 }} key={index}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: colors.bg,
                          border: `1px solid ${alpha(colors.color, 0.2)}`,
                        }}
                      >
                        <Box display="flex" gap={1.5}>
                          <Icon sx={{ color: colors.color, fontSize: 24, flexShrink: 0 }} />
                          <Box flex={1}>
                            <Typography variant="subtitle2" fontWeight={600} color={colors.color} gutterBottom>
                              {insight?.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {insight?.message}
                            </Typography>
                            <Chip label={insight?.category} size="small" sx={{ mt: 1, textTransform: 'capitalize' }} />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid2>
                  );
                })}
              </Grid2>
            </CardContent>
          </StyledCard>
        )}
      </Box>
    </Box>
  );
};

export default EnrichmentPerformancePage;
